import React from "react";
import { Box, Card, Typography, Grid } from "@mui/material";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import pxToRem from "assets/theme/functions/pxToRem";
import { Colors } from "utils/Constants";
import LegalLayout from "./LegalLayout";

const personalInfoItems = [
  "Name",
  "Mobile number",
  "Email address",
  "Date of birth",
  "Address",
  "KYC details (PAN, Aadhaar, etc.)",
  "Financial details required for loan processing",
];

const deviceAppItems = [
  "Device type, model, OS",
  "IP address",
  "App usage data",
  "Cookies and analytics data",
];

const loanAppItems = [
  "Income details",
  "Employment information",
  "Bank statements",
  "Credit-related information",
];

const howWeUseItems = [
  "Provide loan comparison, advisory, and application services",
  "Submit your loan application to partnered banks/NBFCs",
  "Verify your identity and documentation",
  "Improve app performance and user experience",
  "Communicate updates, alerts, and service notifications",
  "Comply with legal and regulatory requirements",
];

const sharingItems = [
  "Partnered banks/NBFCs for loan processing",
  "Trusted third-party service providers (verification, analytics, support)",
  "Regulatory authorities if required by law",
  "Logistics partners (only for delivery of any eligible reward/product, if applicable)",
];

const rightsItems = [
  {
    id: "right-access",
    icon: <PersonOutlineOutlinedIcon sx={{ fontSize: pxToRem(20), color: Colors.PRIMARY }} />,
    text: "Access or update your personal information",
  },
  {
    id: "right-delete",
    icon: <DeleteOutlineOutlinedIcon sx={{ fontSize: pxToRem(20), color: Colors.PRIMARY }} />,
    text: "Request deletion of your data (where legally permissible)",
  },
  {
    id: "right-withdraw",
    icon: <NotificationsOffOutlinedIcon sx={{ fontSize: pxToRem(20), color: Colors.PRIMARY }} />,
    text: "Withdraw consent for communication",
  },
  {
    id: "right-contact",
    icon: <HeadsetMicOutlinedIcon sx={{ fontSize: pxToRem(20), color: Colors.PRIMARY }} />,
    text: "Contact us for any privacy-related queries",
  },
];

const channelItems = [
  {
    id: "chan-sms",
    label: "SMS",
    icon: <MessageOutlinedIcon sx={{ fontSize: pxToRem(16), color: Colors.PRIMARY }} />,
  },
  {
    id: "chan-whatsapp",
    label: "WhatsApp",
    icon: <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: pxToRem(16), color: Colors.PRIMARY }} />,
  },
  {
    id: "chan-email",
    label: "Email",
    icon: <MailOutlineOutlinedIcon sx={{ fontSize: pxToRem(16), color: Colors.PRIMARY }} />,
  },
  {
    id: "chan-phone",
    label: "Phone calls",
    icon: <PhoneOutlinedIcon sx={{ fontSize: pxToRem(16), color: Colors.PRIMARY }} />,
  },
];

function PrivacyPolicy() {
  return (
    <LegalLayout>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Top Breadcrumb & Timestamp Bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: pxToRem(13), color: "#64748B" }}>
              Finvibes Financial Services
            </Typography>
            <Typography sx={{ fontSize: pxToRem(13), color: "#94A3B8" }}>›</Typography>
            <Typography sx={{ fontSize: pxToRem(13), color: Colors.PRIMARY, fontWeight: 600 }}>
              Legal Documentation
            </Typography>
          </Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              backgroundColor: "#EFF6FF",
              border: "1px solid #DBEAFE",
              borderRadius: "20px",
              px: 1.75,
              py: 0.5,
            }}
          >
            <AccessTimeOutlinedIcon sx={{ fontSize: pxToRem(14), color: Colors.PRIMARY }} />
            <Typography sx={{ fontSize: pxToRem(12), color: Colors.PRIMARY, fontWeight: 600 }}>
              Last Updated: October 2026
            </Typography>
          </Box>
        </Box>

        {/* Hero Card */}
        <Card
          sx={{
            p: { xs: 3, sm: 4.5 },
            borderRadius: "16px",
            border: "1px solid #DCE7F8",
            background: `linear-gradient(180deg, #F0F6FE 0%, ${Colors.WHITE} 100%)`,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              backgroundColor: "#DBEAFE",
              borderRadius: "20px",
              px: 1.5,
              py: 0.4,
              mb: 1.5,
            }}
          >
            <SecurityOutlinedIcon sx={{ fontSize: pxToRem(14), color: Colors.PRIMARY }} />
            <Typography sx={{ fontSize: pxToRem(11.5), color: Colors.PRIMARY, fontWeight: 700 }}>
              Official Legal Policy
            </Typography>
          </Box>

          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: pxToRem(24), sm: pxToRem(30) },
              fontWeight: 800,
              color: "#0F172A",
              letterSpacing: "-0.5px",
              mb: 0.5,
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            sx={{
              fontSize: pxToRem(13.5),
              color: "#475467",
              lineHeight: 1.7,
            }}
          >
            Finvibes Financial Services Pvt. Ltd. (&quot;Finvibes&quot;, &quot;we&quot;,
            &quot;our&quot;, &quot;us&quot;) is committed to protecting your personal information.
            This Privacy Policy explains how we collect, use, store, and safeguard your data when
            you use the Finvibes mobile application.
          </Typography>
        </Card>

        {/* Main Document Body */}
        <Card
          sx={{
            p: { xs: 3, sm: 4.5 },
            borderRadius: "16px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
            backgroundColor: Colors.WHITE,
            display: "flex",
            flexDirection: "column",
            gap: 4.5,
          }}
        >
          {/* Section 01 */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <Box
                sx={{
                  backgroundColor: "#EFF6FF",
                  color: Colors.PRIMARY,
                  fontSize: pxToRem(16),
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: "6px",
                }}
              >
                1
              </Box>
              <Typography sx={{ fontSize: pxToRem(16), fontWeight: 700, color: "#0F172A" }}>
                Information We Collect
              </Typography>
            </Box>
            <Typography sx={{ fontSize: pxToRem(13.5), color: "#475467", mb: 2 }}>
              We may collect the following types of information:
            </Typography>

            <Grid container spacing={2}>
              {/* Personal Info */}
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2.5,
                    height: "100%",
                    borderRadius: "12px",
                    border: "1px solid #E2E8F0",
                    backgroundColor: "#F8FAFC",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <PersonOutlineOutlinedIcon
                      sx={{ fontSize: pxToRem(20), color: Colors.PRIMARY }}
                    />
                    <Typography sx={{ fontSize: pxToRem(14), fontWeight: 700, color: "#101828" }}>
                      a. Personal Information
                    </Typography>
                  </Box>
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      pl: 2.2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.75,
                      "& li": { fontSize: pxToRem(13), color: "#475467", lineHeight: 1.5 },
                    }}
                  >
                    {personalInfoItems.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </Box>
                </Box>
              </Grid>

              {/* Device & App Info */}
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2.5,
                    height: "100%",
                    borderRadius: "12px",
                    border: "1px solid #E2E8F0",
                    backgroundColor: "#F8FAFC",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <DevicesOutlinedIcon sx={{ fontSize: pxToRem(20), color: Colors.PRIMARY }} />
                    <Typography sx={{ fontSize: pxToRem(14), fontWeight: 700, color: "#101828" }}>
                      b. Device & App Information
                    </Typography>
                  </Box>
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      pl: 2.2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.75,
                      "& li": { fontSize: pxToRem(13), color: "#475467", lineHeight: 1.5 },
                    }}
                  >
                    {deviceAppItems.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </Box>
                </Box>
              </Grid>

              {/* Loan Application Data */}
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2.5,
                    height: "100%",
                    borderRadius: "12px",
                    border: "1px solid #E2E8F0",
                    backgroundColor: "#F8FAFC",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <ReceiptLongOutlinedIcon
                      sx={{ fontSize: pxToRem(20), color: Colors.PRIMARY }}
                    />
                    <Typography sx={{ fontSize: pxToRem(14), fontWeight: 700, color: "#101828" }}>
                      c. Loan Application Data
                    </Typography>
                  </Box>
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      pl: 2.2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.75,
                      "& li": { fontSize: pxToRem(13), color: "#475467", lineHeight: 1.5 },
                    }}
                  >
                    {loanAppItems.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Section 02 */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <Box
                sx={{
                  backgroundColor: "#EFF6FF",
                  color: Colors.PRIMARY,
                  fontSize: pxToRem(16),
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: "6px",
                }}
              >
                2
              </Box>
              <Typography sx={{ fontSize: pxToRem(16), fontWeight: 700, color: "#0F172A" }}>
                How We Use Your Information
              </Typography>
            </Box>
            <Typography sx={{ fontSize: pxToRem(13.5), color: "#475467", mb: 2 }}>
              We use your information to:
            </Typography>
            <Box
              sx={{
                p: 2.5,
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
                backgroundColor: "#F8FAFC",
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
              }}
            >
              {howWeUseItems.map((item) => (
                <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: pxToRem(18), color: Colors.PRIMARY }} />
                  <Typography sx={{ fontSize: pxToRem(13.5), color: "#334155" }}>{item}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Section 03 */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <Box
                sx={{
                  backgroundColor: "#EFF6FF",
                  color: Colors.PRIMARY,
                  fontSize: pxToRem(16),
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: "6px",
                }}
              >
                3
              </Box>
              <Typography sx={{ fontSize: pxToRem(16), fontWeight: 700, color: "#0F172A" }}>
                Sharing of Information
              </Typography>
            </Box>

            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                backgroundColor: "#EFF6FF",
                border: "1px solid #DBEAFE",
                borderRadius: "6px",
                px: 1.5,
                py: 0.5,
                mb: 1.5,
              }}
            >
              <SecurityOutlinedIcon sx={{ fontSize: pxToRem(15), color: Colors.PRIMARY }} />
              <Typography sx={{ fontSize: pxToRem(13), fontWeight: 600, color: Colors.PRIMARY }}>
                Finvibes does not sell or rent your data.
              </Typography>
            </Box>

            <Typography sx={{ fontSize: pxToRem(13.5), color: "#475467", mb: 1.5 }}>
              We may share your information only with:
            </Typography>

            <Box
              sx={{
                p: 2.5,
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
                backgroundColor: "#F8FAFC",
              }}
            >
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  "& li": { fontSize: pxToRem(13.5), color: "#334155" },
                }}
              >
                {sharingItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Section 04 */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <Box
                sx={{
                  backgroundColor: "#EFF6FF",
                  color: Colors.PRIMARY,
                  fontSize: pxToRem(16),
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: "6px",
                }}
              >
                4
              </Box>
              <Typography sx={{ fontSize: pxToRem(16), fontWeight: 700, color: "#0F172A" }}>
                Data Security
              </Typography>
            </Box>
            <Box
              sx={{
                p: 2.5,
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
                backgroundColor: "#F8FAFC",
              }}
            >
              <Typography sx={{ fontSize: pxToRem(13.5), color: "#334155", lineHeight: 1.7 }}>
                We use industry-standard security measures to protect your data. However, no method
                of transmission or storage is 100% secure. We encourage you to keep your login
                details confidential.
              </Typography>
            </Box>
          </Box>

          {/* Section 05 */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <Box
                sx={{
                  backgroundColor: "#EFF6FF",
                  color: Colors.PRIMARY,
                  fontSize: pxToRem(16),
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: "6px",
                }}
              >
                5
              </Box>
              <Typography sx={{ fontSize: pxToRem(16), fontWeight: 700, color: "#0F172A" }}>
                Your Rights
              </Typography>
            </Box>
            <Typography sx={{ fontSize: pxToRem(13.5), color: "#475467", mb: 2 }}>
              You can:
            </Typography>

            <Grid container spacing={2}>
              {rightsItems.map((item) => (
                <Grid item xs={12} sm={6} key={item.id}>
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      borderRadius: "10px",
                      border: "1px solid #E2E8F0",
                      backgroundColor: "#F8FAFC",
                    }}
                  >
                    {item.icon}
                    <Typography sx={{ fontSize: pxToRem(13.5), color: "#334155", fontWeight: 500 }}>
                      {item.text}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Section 06 */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <Box
                sx={{
                  backgroundColor: "#EFF6FF",
                  color: Colors.PRIMARY,
                  fontSize: pxToRem(16),
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: "6px",
                }}
              >
                6
              </Box>
              <Typography sx={{ fontSize: pxToRem(16), fontWeight: 700, color: "#0F172A" }}>
                Communication Consent
              </Typography>
            </Box>
            <Typography sx={{ fontSize: pxToRem(13.5), color: "#475467", mb: 2 }}>
              By using the Finvibes app, you consent to receive communication via:
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
              {channelItems.map((chan) => (
                <Box
                  key={chan.id}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 2,
                    py: 0.75,
                    borderRadius: "20px",
                    border: "1px solid #DBEAFE",
                    backgroundColor: "#EFF6FF",
                  }}
                >
                  {chan.icon}
                  <Typography
                    sx={{ fontSize: pxToRem(13), fontWeight: 600, color: Colors.PRIMARY }}
                  >
                    {chan.label}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography sx={{ fontSize: pxToRem(12.5), color: "#64748B" }}>
              This is required for loan updates, reminders, and service notifications.
            </Typography>
          </Box>

          {/* Section 07 */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <Box
                sx={{
                  backgroundColor: "#EFF6FF",
                  color: Colors.PRIMARY,
                  fontSize: pxToRem(16),
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: "6px",
                }}
              >
                7
              </Box>
              <Typography sx={{ fontSize: pxToRem(16), fontWeight: 700, color: "#0F172A" }}>
                Third-Party Links
              </Typography>
            </Box>
            <Box
              sx={{
                p: 2.5,
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
                backgroundColor: "#F8FAFC",
              }}
            >
              <Typography sx={{ fontSize: pxToRem(13.5), color: "#334155", lineHeight: 1.7 }}>
                Our app may contain links to third-party websites or apps. Finvibes is not
                responsible for their privacy practices or content.
              </Typography>
            </Box>
          </Box>

          {/* Section 08 */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <Box
                sx={{
                  backgroundColor: "#EFF6FF",
                  color: Colors.PRIMARY,
                  fontSize: pxToRem(16),
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: "6px",
                }}
              >
                8
              </Box>
              <Typography sx={{ fontSize: pxToRem(16), fontWeight: 700, color: "#0F172A" }}>
                Changes to this Policy
              </Typography>
            </Box>
            <Box
              sx={{
                p: 2.5,
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
                backgroundColor: "#F8FAFC",
              }}
            >
              <Typography sx={{ fontSize: pxToRem(13.5), color: "#334155", lineHeight: 1.7 }}>
                We may update this Privacy Policy from time to time. Continued use of the app after
                an update means you accept the revised policy.
              </Typography>
            </Box>
          </Box>

          {/* Section 09 */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <Box
                sx={{
                  backgroundColor: "#EFF6FF",
                  color: Colors.PRIMARY,
                  fontSize: pxToRem(16),
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: "6px",
                }}
              >
                9
              </Box>
              <Typography sx={{ fontSize: pxToRem(16), fontWeight: 700, color: "#0F172A" }}>
                Contact Us
              </Typography>
            </Box>
            <Typography sx={{ fontSize: pxToRem(13.5), color: "#475467", mb: 2 }}>
              For any questions, concerns, or privacy requests, contact:
            </Typography>

            <Box
              component="a"
              href="mailto:connect@finvibes.net"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.5,
                p: 2,
                borderRadius: "12px",
                border: "1px solid #DBEAFE",
                backgroundColor: "#EFF6FF",
                textDecoration: "none",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "#DBEAFE",
                },
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  backgroundColor: Colors.PRIMARY,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: Colors.WHITE,
                }}
              >
                <MailOutlineOutlinedIcon sx={{ fontSize: pxToRem(18) }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: pxToRem(11.5), color: "#64748B", fontWeight: 600 }}>
                  Direct Email
                </Typography>
                <Typography sx={{ fontSize: pxToRem(14), fontWeight: 700, color: Colors.PRIMARY }}>
                  connect@finvibes.net →
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Important Callout Banner */}
          <Box
            sx={{
              mt: 1,
              p: 2.5,
              borderRadius: "12px",
              backgroundColor: "#EEF4FF",
              border: "1px solid #D0E1FD",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                backgroundColor: "#DBEAFE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <VerifiedUserOutlinedIcon sx={{ color: Colors.PRIMARY, fontSize: pxToRem(22) }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: pxToRem(14.5),
                  fontWeight: 700,
                  color: "#1E3A8A",
                  lineHeight: 1.3,
                }}
              >
                Important
              </Typography>
              <Typography
                sx={{
                  fontSize: pxToRem(13),
                  color: Colors.PRIMARY,
                  mt: 0.25,
                }}
              >
                Please read this privacy policy carefully before using the app.
              </Typography>
            </Box>
          </Box>
        </Card>
      </Box>
    </LegalLayout>
  );
}

export default PrivacyPolicy;
